import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Mail, Lock, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password, phone);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate(location.state?.from || "/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-[80vh] bg-[#F8FAFC] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-navy-gradient flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-2xl font-bold text-[#0B3C5D]">
              Gifto<span className="text-[#00C853]">pay</span>
            </span>
          </Link>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_8px_32px_rgba(11,60,93,0.06)] p-8">
          <h1 className="font-display text-2xl font-bold text-[#0B3C5D]">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to track and manage your payouts.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Phone number</label>
              <div className="mt-1.5 relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  inputMode="tel"
                  autoComplete="tel"
                  className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                  data-testid="login-phone"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Password</label>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#0B3C5D] text-white py-3.5 font-semibold btn-primary disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              state={location.state}
              className="text-[#0B3C5D] font-semibold hover:text-[#00C853]"
              data-testid="login-to-register"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
